import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatCurrency(
  amount: number,
  currency: 'USD' | 'THB' = 'THB',
  options?: Intl.NumberFormatOptions & { compact?: boolean }
): string {
  // Handle compact format
  if (options?.compact) {
    const compactValue = formatCompactNumber(amount)
    return `${compactValue} ${currency}`
  }

  const defaultOptions: Intl.NumberFormatOptions = {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2, // Fix to 2 decimal places for currency display
  }

  const formatOptions = { ...defaultOptions, ...options }

  const formattedNumber = new Intl.NumberFormat('en-US', formatOptions).format(amount)
  return `${formattedNumber} ${currency}`
}

export function formatPercent(
  value: number,
  options?: Intl.NumberFormatOptions
): string {
  const defaultOptions: Intl.NumberFormatOptions = {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }

  const formatOptions = { ...defaultOptions, ...options }
  
  return new Intl.NumberFormat('en-US', formatOptions).format(value / 100)
}

export function formatNumber(
  value: number,
  options?: Intl.NumberFormatOptions
): string {
  const defaultOptions: Intl.NumberFormatOptions = {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }

  const formatOptions = { ...defaultOptions, ...options }
  
  return new Intl.NumberFormat('en-US', formatOptions).format(value)
}

export function formatCompactNumber(value: number): string {
  if (value >= 1e9) {
    return (value / 1e9).toFixed(1) + 'B'
  } else if (value >= 1e6) {
    return (value / 1e6).toFixed(1) + 'M'
  } else if (value >= 1e3) {
    return (value / 1e3).toFixed(1) + 'K'
  }
  return value.toFixed(0)
}

export function getStockTypeInfo(portApiType: 'us_stock' | 'th_stock' | 'th_fund' | 'us_fund' | 'gold') {
  const typeMap = {
    us_stock: {
      label: 'US Stock',
      labelTh: 'หุ้นสหรัฐ',
      flag: '🇺🇸',
      color: 'blue',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      textColor: 'text-blue-700 dark:text-blue-300',
      borderColor: 'border-blue-200 dark:border-blue-700'
    },
    th_stock: {
      label: 'Thai Stock',
      labelTh: 'หุ้นไทย',
      flag: '🇹🇭',
      color: 'orange',
      bgColor: 'bg-orange-50 dark:bg-orange-900/20',
      textColor: 'text-orange-700 dark:text-orange-300',
      borderColor: 'border-orange-200 dark:border-orange-700'
    },
    th_fund: {
      label: 'Thai Fund',
      labelTh: 'กองทุนไทย',
      flag: '🏦',
      color: 'green',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      textColor: 'text-green-700 dark:text-green-300',
      borderColor: 'border-green-200 dark:border-green-700'
    },
    us_fund: {
      label: 'US Fund',
      labelTh: 'กองทุนสหรัฐ',
      flag: '🏛️',
      color: 'purple',
      bgColor: 'bg-purple-50 dark:bg-purple-900/20',
      textColor: 'text-purple-700 dark:text-purple-300',
      borderColor: 'border-purple-200 dark:border-purple-700'
    },
    gold: {
      label: 'Gold',
      labelTh: 'ทองคำ',
      flag: '🥇',
      color: 'yellow',
      bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
      textColor: 'text-yellow-700 dark:text-yellow-300',
      borderColor: 'border-yellow-200 dark:border-yellow-700'
    }
  }

  return typeMap[portApiType]
}

export function getCountryInfo(portApiType: 'us_stock' | 'th_stock' | 'th_fund' | 'us_fund' | 'gold') {
  const countryMap = {
    us_stock: {
      country: 'US',
      label: 'United States',
      labelTh: 'สหรัฐอเมริกา',
      flag: '🇺🇸',
      color: 'blue',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      textColor: 'text-blue-700 dark:text-blue-300',
      borderColor: 'border-blue-200 dark:border-blue-700'
    },
    us_fund: {
      country: 'US',
      label: 'United States',
      labelTh: 'สหรัฐอเมริกา',
      flag: '🇺🇸',
      color: 'blue',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      textColor: 'text-blue-700 dark:text-blue-300',
      borderColor: 'border-blue-200 dark:border-blue-700'
    },
    th_stock: {
      country: 'TH',
      label: 'Thailand',
      labelTh: 'ประเทศไทย',
      flag: '🇹🇭',
      color: 'orange',
      bgColor: 'bg-orange-50 dark:bg-orange-900/20',
      textColor: 'text-orange-700 dark:text-orange-300',
      borderColor: 'border-orange-200 dark:border-orange-700'
    },
    th_fund: {
      country: 'TH',
      label: 'Thailand',
      labelTh: 'ประเทศไทย',
      flag: '🇹🇭',
      color: 'orange',
      bgColor: 'bg-orange-50 dark:bg-orange-900/20',
      textColor: 'text-orange-700 dark:text-orange-300',
      borderColor: 'border-orange-200 dark:border-orange-700'
    },
    gold: {
      country: 'GLOBAL',
      label: 'Global',
      labelTh: 'ทั่วโลก',
      flag: '🌍',
      color: 'yellow',
      bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
      textColor: 'text-yellow-700 dark:text-yellow-300',
      borderColor: 'border-yellow-200 dark:border-yellow-700'
    }
  }

  return countryMap[portApiType]
}

export function getSectorInfo(sector: string, sectors?: any[]) {
  // If sectors are provided directly, use them first
  if (sectors && sectors.length > 0) {
    const sectorFromArray = sectors.find(s => s.key === sector)
    if (sectorFromArray) {
      return {
        label: sectorFromArray.label,
        icon: sectorFromArray.icon,
        color: sectorFromArray.color,
        bgColor: sectorFromArray.bg_color,
        textColor: sectorFromArray.text_color,
        borderColor: sectorFromArray.border_color
      }
    }
  }

  // Try to get sector from database via global store
  try {
    const portfolioStore = (globalThis as any).portfolioStore
    if (portfolioStore && portfolioStore.getSectorByKey) {
      const sectorFromDB = portfolioStore.getSectorByKey(sector)
      
      if (sectorFromDB) {
        return {
          label: sectorFromDB.label,
          icon: sectorFromDB.icon,
          color: sectorFromDB.color,
          bgColor: sectorFromDB.bg_color,
          textColor: sectorFromDB.text_color,
          borderColor: sectorFromDB.border_color
        }
      }
    }
  } catch (error) {
    // Fallback to hardcoded values if store is not available
  }

  // Fallback to hardcoded values for backward compatibility
  const sectorMap: Record<string, {
    label: string
    icon: string
    color: string
    bgColor: string
    textColor: string
    borderColor: string
  }> = {
    technology: {
      label: 'Technology',
      icon: '💻',
      color: 'blue',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      textColor: 'text-blue-700 dark:text-blue-300',
      borderColor: 'border-blue-200 dark:border-blue-700'
    },
    healthcare: {
      label: 'Healthcare',
      icon: '🏥',
      color: 'red',
      bgColor: 'bg-red-50 dark:bg-red-900/20',
      textColor: 'text-red-700 dark:text-red-300',
      borderColor: 'border-red-200 dark:border-red-700'
    },
    finance: {
      label: 'Finance',
      icon: '🏦',
      color: 'green',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      textColor: 'text-green-700 dark:text-green-300',
      borderColor: 'border-green-200 dark:border-green-700'
    },
    energy: {
      label: 'Energy',
      icon: '⚡',
      color: 'yellow',
      bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
      textColor: 'text-yellow-700 dark:text-yellow-300',
      borderColor: 'border-yellow-200 dark:border-yellow-700'
    },
    consumer: {
      label: 'Consumer Goods',
      icon: '🛒',
      color: 'purple',
      bgColor: 'bg-purple-50 dark:bg-purple-900/20',
      textColor: 'text-purple-700 dark:text-purple-300',
      borderColor: 'border-purple-200 dark:border-purple-700'
    },
    industrial: {
      label: 'Industrial',
      icon: '🏭',
      color: 'orange',
      bgColor: 'bg-orange-50 dark:bg-orange-900/20',
      textColor: 'text-orange-700 dark:text-orange-300',
      borderColor: 'border-orange-200 dark:border-orange-700'
    },
    utilities: {
      label: 'Utilities',
      icon: '🔌',
      color: 'indigo',
      bgColor: 'bg-indigo-50 dark:bg-indigo-900/20',
      textColor: 'text-indigo-700 dark:text-indigo-300',
      borderColor: 'border-indigo-200 dark:border-indigo-700'
    },
    materials: {
      label: 'Materials',
      icon: '🧱',
      color: 'gray',
      bgColor: 'bg-gray-50 dark:bg-gray-900/20',
      textColor: 'text-gray-700 dark:text-gray-300',
      borderColor: 'border-gray-200 dark:border-gray-700'
    },
    telecom: {
      label: 'Telecommunications',
      icon: '📡',
      color: 'cyan',
      bgColor: 'bg-cyan-50 dark:bg-cyan-900/20',
      textColor: 'text-cyan-700 dark:text-cyan-300',
      borderColor: 'border-cyan-200 dark:border-cyan-700'
    },
    realestate: {
      label: 'Real Estate',
      icon: '🏢',
      color: 'emerald',
      bgColor: 'bg-emerald-50 dark:bg-emerald-900/20',
      textColor: 'text-emerald-700 dark:text-emerald-300',
      borderColor: 'border-emerald-200 dark:border-emerald-700'
    },
    world: {
      label: 'World',
      icon: '🌍',
      color: 'teal',
      bgColor: 'bg-teal-50 dark:bg-teal-900/20',
      textColor: 'text-teal-700 dark:text-teal-300',
      borderColor: 'border-teal-200 dark:border-teal-700'
    },
    other: {
      label: 'Other',
      icon: '📊',
      color: 'slate',
      bgColor: 'bg-slate-50 dark:bg-slate-900/20',
      textColor: 'text-slate-700 dark:text-slate-300',
      borderColor: 'border-slate-200 dark:border-slate-700'
    }
  }

  return sectorMap[sector] || sectorMap.other
}

export function getGainLossColor(value: number): {
  textColor: string
  bgColor: string
  icon: string
} {
  if (value > 0) {
    return {
      textColor: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      icon: '📈'
    }
  } else if (value < 0) {
    return {
      textColor: 'text-red-600 dark:text-red-400',
      bgColor: 'bg-red-50 dark:bg-red-900/20',
      icon: '📉'
    }
  } else {
    return {
      textColor: 'text-gray-600 dark:text-gray-400',
      bgColor: 'bg-gray-50 dark:bg-gray-900/20',
      icon: '➖'
    }
  }
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null

  return (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout)
    }
    
    timeout = setTimeout(() => {
      func(...args)
    }, wait)
  }
}

export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean = false

  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => {
        inThrottle = false
      }, limit)
    }
  }
}

export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export function generateId(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36)
}

export function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text).then(() => true).catch(() => false)
  } else {
    // Fallback for older browsers
    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.style.position = 'absolute'
    textArea.style.left = '-999999px'
    
    document.body.prepend(textArea)
    textArea.select()
    
    try {
      document.execCommand('copy')
      return Promise.resolve(true)
    } catch (error) {
      return Promise.resolve(false)
    } finally {
      textArea.remove()
    }
  }
}

export function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()
  return `${day}/${month}/${year}`
}

export function getRelativeTime(date: Date | string): string {
  const now = new Date()
  const target = new Date(date)
  const diffInSeconds = Math.floor((now.getTime() - target.getTime()) / 1000)

  if (diffInSeconds < 60) {
    return 'just now'
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60)
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600)
    return `${hours} hour${hours > 1 ? 's' : ''} ago`
  } else {
    const days = Math.floor(diffInSeconds / 86400)
    return `${days} day${days > 1 ? 's' : ''} ago`
  }
}

export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

export function isValidUrl(url: string): boolean {
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

export function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength) + '...'
}

export function removeNullUndefined<T>(obj: T): T {
  const result = {} as T
  
  for (const key in obj) {
    const value = obj[key]
    if (value !== null && value !== undefined) {
      result[key] = value
    }
  }
  
  return result
}